import { AxiosError } from 'axios';
import constate from 'constate';
import dayjs, { Dayjs } from 'dayjs';
import { isEmpty, isFunction } from 'lodash';
import {
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DB_DATE_FORMAT, EXTERNAL_REQUEST_ERROR_CODE } from 'core/constants';
import { ApiStatus, NotificationPosition } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import { useNotificationContext, useStepsContext } from 'core/states';
import { AxiosErrorData, IAutocompleteOption, INotification, IOption } from 'core/types';
import {
  isEnterKeyPress,
  isShiftEnterKeyPress,
  isSubmitButtonBlur,
  isTabKeyPress,
  markSubmitIntent,
} from 'core/utils';
import { PartNumberHelper } from 'parts/helpers';
import { useGetUoms } from 'parts/hooks';
import { IManufacturer, IPart } from 'parts/types';
import {
  findHeldAgreement,
  getTradeAgreementRowId,
  getTransientLine,
  isTradeAgreementsLineError,
  withTradeAgreementsLineMessage,
} from 'quotes/components/QuotePartSelectSpa/utils';
import { PriceBasedOn, QuotePartUnifiedStatus, QuoteType } from 'quotes/enums';
import { EMPTY_VENDOR_QUOTE_COST_DATA, QuotePartHelper } from 'quotes/helpers';
import { useFetchTradeAgreements } from 'quotes/hooks';
import {
  IPartConfig,
  IPartValue,
  ISelectedSpaFormValue,
  ITradeAgreementLine,
  IValidatePart,
} from 'quotes/types';

import { usePricingStateContext } from './CostSection/CostFormFields';

enum FieldName {
  CORPORATE_LOCATION = 'corporateLocation',
  COST_SOURCE_VENDOR_QUOTE_NUMBER = 'costSource.vendorQuoteNumber',
  DESCRIPTION = 'description',
  IS_REPLACEMENT_COST_CUSTOM = 'isReplacementCostCustom',
  MANUFACTURER = 'manufacturer',
  MARGIN = 'margin',
  PART = 'part',
  PRICE = 'price',
  PRICE_BASED_ON = 'priceBasedOn',
  PRICING_MARGIN = 'pricingMargin',
  PURCHASE_COST = 'purchaseCost',
  QUANTITY = 'quantity',
  REPLACEMENT_COST = 'replacementCost',
  REPLACEMENT_COST_NUMBER = 'replacementCostNumber',
  REPLACEMENT_STANDARD_COST = 'replacementStandardCost',
  SHIPMENT_TYPE = 'shipmentType',
  STANDARD_COST = 'standardCost',
  UOM = 'uom',
}

const mapUomOptions = (uoms?: string[]) =>
  uoms?.map((uom: string) => ({
    name: uom,
  }));

const useUpsertLineForm = ({
  isOpen,
  onClose,
  entityId,
  quoteType,
  entityLineId,
  partId,
  partValue,
  manufacturerValue,
  descriptionValue,
  quantityValue,
  purchaseCostValue,
  uomValue,
  reset,
  getValues,
  setValue,
  validatePartAction,
  disabledPriceCall,
  setHasExternalTaxonomy,
  onSubmit,
  saveAndAddNextOptions,
  setValidatePartTaxonomyPath,
  onValidatingChange = null,
  initialPart = null,
}) => {
  const [hintOpen, setHintOpen] = useState(true);

  const [unverifiedPartNumber, setUnverifiedPartNumber] = useState<string>('');
  const [verifiedPart, setVerifiedPart] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [config, setConfig] = useState<IPartConfig | null>(null);
  const [enabledPricesCall, setEnabledPricesCall] = useState(!disabledPriceCall);
  const [defaultShippingFields, setDefaultShippingFields] = useState<{
    shipmentTypeId: number | null;
    corporateLocationId: number | null;
  }>({
    shipmentTypeId: null,
    corporateLocationId: null,
  });
  const [previousQuantity, setPreviousQuantity] = useState<number | null>(null);
  const [uomOptions, setUomOptions] = useState<IAutocompleteOption[]>([]);
  const [quantityWarnings, setQuantityWarnings] = useState<string[]>([]);
  const [focusInput, setFocusInput] = useState<FieldName>(FieldName.PART);
  const selectedSpaReaskIdRef = useRef(0);

  const { t } = useTranslation();

  const {
    clearNotification,
    getNotification,
    showNotification,
    showErrorNotification,
    getIsLoadingNotificationDisplayed,
  } = useNotificationContext();

  const { goToNextStep, isLoadingNextStep } = useStepsContext();

  const {
    costData,
    setCostData,
    setPriceData,
    setPriceDecimals,
    setUnifiedPartStatus,
    setExternalDisabled,
    isCalculating,
    isAlternateVersionEnabled,
    processPricingResponse,
    calculatePrices: costContextCalculatePrices,
    clearTypedCostValidity,
    initializePreviousPricingValues,
    resetQuantityBreaks,
    resetSpaSelectionAvailable,
    forceNextPrices,
    selectSpa,
    setIsSelectedSpaNotOffered,
    buildSelectSpaRequest,
    isSelectSpaPressed,
    pricingWindowMessage,
  } = usePricingStateContext();

  const fetchTradeAgreements = useFetchTradeAgreements();

  const displayReplacementCost = useMemo(
    () =>
      QuotePartHelper.displayReplacementCost({
        isAlternateVersionEnabled,
        isShipAndDebitVendor: partValue?.supplier?.isShipAndDebit || false,
      }),
    [isAlternateVersionEnabled, partValue?.supplier?.isShipAndDebit]
  );

  const { records: uoms } = useGetUoms(
    {
      filters: {
        bomDisplay: 1,
      },
    },
    isOpen
  );

  const emptyPartValues = useMemo(
    () => isValidating || isEmpty(partValue || manufacturerValue || descriptionValue),
    [isValidating, partValue, manufacturerValue, descriptionValue]
  );

  const isLoadingNotificationDisplayed = useMemo(
    () => getIsLoadingNotificationDisplayed(NotificationPosition.DRAWER),
    [getIsLoadingNotificationDisplayed]
  );

  // Save and close lock on this, not only on the loading toast: another toast would free them.
  const isBusy = isValidating || isCalculating;

  useEffect(() => {
    if (!isBusy) {
      return;
    }

    onValidatingChange?.(true);

    return () => onValidatingChange?.(false);
  }, [isBusy, onValidatingChange]);

  // While a save is in flight (or any drawer-level load is showing) every field must lock so the
  // user can't edit a form whose payload the submit path already captured. Mirrors the Save
  // button's own disable signal in StepButtons (drawer loading notification + step transition).
  const isSubmitting = isLoadingNotificationDisplayed || isLoadingNextStep;

  // Feeds the cost/price/shipping sections, which all read `disablePricingFields` off the
  // pricing state.
  // The detail-step fields (part, manufacturer, description, quantity, uom) read their own
  // signals, so they OR in `isSubmitting` directly (see LineDetailsForm).
  // The pricing preview raises the same drawer loading notification as a save, so exclude it here
  // (`isCalculating`): pricing-coupled fields already lock through disablePricingFields, the
  // detail-step trigger fields still lock through their direct `isSubmitting` reads, and the
  // Vendor Quote / Agreement Number inputs — gated on isExternalDisabled alone — must stay
  // editable while the prices call runs. Assumes a save/step-transition cannot START while a calc
  // is in flight — StepButtons and the Enter-submit guard disable on the very notification the
  // calc raises — so the exclusion never leaves those inputs editable during an actual save.
  useEffect(() => {
    setExternalDisabled(emptyPartValues || (isSubmitting && !isCalculating));
  }, [emptyPartValues, isCalculating, isSubmitting, setExternalDisabled]);

  const setFormValue = useCallback(
    (
      name: FieldName,
      value: string | number | boolean | Dayjs | ((prev: unknown) => unknown) | null
    ) =>
      setValue(
        name,
        isFunction(value) ? (value as (prev: unknown) => unknown)(getValues(name)) : value,
        {
          shouldValidate: !!value,
        }
      ),
    [getValues, setValue]
  );

  const validateQuantity = useCallback(
    (quantity: number, uom: string) => {
      const minimumQuantityInQuoteUom =
        config?.[uom]?.minimumQuantityInQuoteUom || partValue?.minimumQuantityInQuoteUom;
      const orderIncrementInQuoteUom =
        config?.[uom]?.orderIncrementInQuoteUom || partValue?.orderIncrementInQuoteUom;

      const quantityWarnings = [];
      if (minimumQuantityInQuoteUom && quantity && minimumQuantityInQuoteUom > Number(quantity)) {
        quantityWarnings.push(
          t('warningValidationQuantityBelowMinimum', { minimum: minimumQuantityInQuoteUom })
        );
      }

      if (
        orderIncrementInQuoteUom &&
        !NumberHelper.isIncrement(Number(quantity), orderIncrementInQuoteUom)
      ) {
        quantityWarnings.push(
          t('warningValidationQuantityNotMultipleOfIncrement', {
            increment: orderIncrementInQuoteUom,
          })
        );
      }

      setQuantityWarnings(quantityWarnings);
    },
    [config, partValue?.minimumQuantityInQuoteUom, partValue?.orderIncrementInQuoteUom, t]
  );

  const onResetValidatedPartResponse = useCallback(() => {
    setVerifiedPart(false);
    setUnverifiedPartNumber(null);
    setConfig(null);
    setUnifiedPartStatus(null);
  }, [setUnifiedPartStatus]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setPreviousQuantity(Number(getValues(FieldName.QUANTITY)));
    initializePreviousPricingValues();
    // getValues should not be included as a dependency we need to set the previous pricing values
    // on the initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityLineId, isOpen]);

  useEffect(() => {
    onResetValidatedPartResponse();
  }, [entityLineId, onResetValidatedPartResponse]);

  useEffect(() => {
    if (!uomOptions?.length) {
      setUomOptions(uoms as unknown as IAutocompleteOption[]);
    }
  }, [uomOptions?.length, uoms, uoms?.length]);

  useEffect(() => {
    if (!uomValue) {
      setFormValue(FieldName.UOM, emptyPartValues ? null : uomOptions?.[0]?.name);
    }
  }, [emptyPartValues, setFormValue, uomOptions, uomValue]);

  useEffect(() => {
    if (partValue?.verified) {
      setVerifiedPart(true);
      validateQuantity(quantityValue, uomValue);
    }

    if (partValue?.uoms) {
      setUomOptions(mapUomOptions(partValue?.uoms) as unknown as IAutocompleteOption[]);
    }
  }, [partValue?.verified, partValue?.uoms, validateQuantity, quantityValue, uomValue]);

  const onValidatedPartResponse = useCallback(
    (validatedPartResponse: IValidatePart) => {
      const validatedPart = validatedPartResponse?.part;

      // A preset part arrives with the unit its quantity was picked in; keep that unit when the
      // part allows it. A part picked in the form starts on the part's first unit, as before.
      const presetUom = initialPart ? getValues(FieldName.UOM) : null;
      const lineUom =
        presetUom && validatedPartResponse?.uoms?.includes(presetUom)
          ? presetUom
          : validatedPartResponse?.uoms?.[0];

      reset({
        id: partId || '',
        part: {
          ...getValues('part'),
          ...validatedPart,
          number: PartNumberHelper.toValue(validatedPart?.number || getValues('part')?.number),
          shipmentType: validatedPartResponse?.shipmentType,
          supplier: validatedPartResponse?.supplier || null,
          alternateVendorPartNumber: validatedPartResponse?.externalPart?.partNumber,
          externalPartStatus: validatedPartResponse?.externalPart?.partStatus,
          marketingMemo: validatedPartResponse?.externalPart?.marketingMemo,
        },
        description:
          validatedPart?.descriptionLong || validatedPart?.description || getValues('description'),
        manufacturer: validatedPart?.manufacturerId
          ? {
              id: validatedPart?.manufacturerId,
              name: validatedPart?.manufacturerName,
            }
          : getValues('manufacturer'),
        quantity: getValues('quantity') || null,
        uom: lineUom,
        shipmentType: validatedPartResponse?.shipmentType,
        corporateLocation: validatedPartResponse?.corporateLocation || null,
        companyManufacturerName:
          validatedPartResponse?.companyManufacturer?.companyManufacturerName || null,
        companyManufacturerId:
          validatedPartResponse?.companyManufacturer?.companyManufacturerId || null,
        accountsPayableId: validatedPartResponse?.companyManufacturer?.accountsPayableId || null,
        pricingDate: getValues('pricingDate'),
        pricingStrategy: getValues('pricingStrategy'),
        // Vendor: validate-part carries no `prices` block and the drawer renders no CostSection,
        // so nothing re-seeds `price` (the grid's Unit Cost) after this whitelist reset. On
        // customer the clear is deliberate — processPricingResponse below re-seeds the new part's
        // numbers, and carrying the old value over would leak the previous part's price.
        ...(QuoteType.VENDOR === quoteType && { price: getValues(FieldName.PRICE) }),
      });
      clearTypedCostValidity();

      const externalPart = validatedPartResponse?.externalPart;

      setUnifiedPartStatus(
        externalPart?.ddpItemFlag ? QuotePartUnifiedStatus.ACTIVE : QuotePartUnifiedStatus.INACTIVE
      );

      setPriceDecimals(
        (prev: number) => validatedPartResponse?.corporatePart?.quotePriceDecimals || prev
      );
      setVerifiedPart(Boolean(validatedPart?.verified));
      setFocusInput(
        validatedPart?.verified
          ? FieldName.QUANTITY
          : getValues('manufacturer')
          ? FieldName.DESCRIPTION
          : FieldName.MANUFACTURER
      );
      setUnverifiedPartNumber(validatedPart?.number);
      setConfig(validatedPartResponse?.config);

      setDefaultShippingFields({
        shipmentTypeId: Number(validatedPartResponse?.shipmentType?.id),
        corporateLocationId: Number(validatedPartResponse?.corporateLocation?.id),
      });

      setPreviousQuantity(Number(getValues(FieldName.QUANTITY)));
      initializePreviousPricingValues();

      const hasExternalTaxonomy = externalPart?.hasTaxonomy;

      if (setHasExternalTaxonomy) {
        setHasExternalTaxonomy(hasExternalTaxonomy);
      }

      if (!hasExternalTaxonomy && setValidatePartTaxonomyPath) {
        setValidatePartTaxonomyPath(validatedPartResponse?.taxonomyPath);
      }

      // Process prices from validation response
      const uom = lineUom || getValues('uom');
      const pricesUom = validatedPartResponse?.prices?.[uom];

      if (pricesUom) {
        processPricingResponse(pricesUom, true);

        if (pricesUom.externalReplacementCost) {
          setFormValue(FieldName.PART, (prev: Partial<IPartValue>) => ({
            ...prev,
            externalReplacementCost: pricesUom.externalReplacementCost,
            externalReplacementStandardCost: pricesUom.externalReplacementStandardCost,
            replacementStandardCost: pricesUom.replacementStandardCost,
            externalRepCostRegistrationNum: pricesUom.externalRepCostRegistrationNum,
          }));
        }

        if (quantityValue && pricesUom.purchaseCost) {
          const currentPriceBasedOn = getValues(FieldName.PRICE_BASED_ON);

          setFocusInput(
            currentPriceBasedOn === PriceBasedOn.REPLACEMENT
              ? FieldName.PRICING_MARGIN
              : FieldName.MARGIN
          );
        }
      } else {
        // No price data for the selected UOM (e.g. an unverified T-Item): processCostResponse
        // never runs, so enable Overwrite Quoted Cost to make the cost field editable.
        setValue('isCostingCustom', true);
      }
    },
    [
      reset,
      partId,
      getValues,
      clearTypedCostValidity,
      setHasExternalTaxonomy,
      setValidatePartTaxonomyPath,
      setUnifiedPartStatus,
      setPriceDecimals,
      initializePreviousPricingValues,
      processPricingResponse,
      setFormValue,
      quantityValue,
      setValue,
      quoteType,
      initialPart,
    ]
  );

  const onChangePartNumber = async (option: Partial<IPart> & IAutocompleteOption) => {
    setUnifiedPartStatus(null);

    if (!option) {
      handleOnClear();

      return;
    }

    // A typed number comes in display form; the form keeps the stored one. Without `isNew` the
    // field labels the value through `getLabel` (display form) instead of echoing it raw.
    const { isNew, ...selectedOption } = option;
    const part = {
      ...selectedOption,
      number: option.number ? PartNumberHelper.toValue(option.number) : option.vendorNumber,
    };

    setTimeout(() => {
      setValue('part', part);

      if (!option.manufacturerId && option.manufacturerName) {
        // Undefined manufacturer
        setValue('manufacturer', {
          name: option.manufacturerName,
          title: option.manufacturerName,
          isNew: true,
        });
      }

      if (option.manufacturerId && !option.number) {
        // Partfiniti manufacturer for new part
        setValue('manufacturer', {
          name: option.manufacturerName,
          id: option.manufacturerId,
        });
      }
    });

    validatePart(part, isNew ? getValues('manufacturer') : null, isNew);
  };

  // A preset part is validated once per opening, exactly as picking it in the part number field
  // would, so it arrives with its units, pricing and shipping defaults.
  useEffect(() => {
    if (!isOpen || !initialPart?.number) {
      return;
    }

    onChangePartNumber(initialPart);
    // onChangePartNumber is rebuilt every render; re-running on it would validate on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialPart]);

  const onKeyDownPartNumber = (event: KeyboardEvent<HTMLInputElement>, value: string) => {
    if (isEnterKeyPress(event) || isTabKeyPress(event)) {
      event.preventDefault();

      onChangePartNumber({ number: value });
    }
  };

  const onChangeManufacturer = async (option: IManufacturer) => {
    if (!option || !getValues('part')) {
      return;
    }

    validatePart(getValues('part'), option);
  };

  const onChangeQuantity = ({ target: { value } }) => {
    validateQuantity(value, uomValue);
  };

  const getFocusFieldAfterPricing = () => {
    const purchaseCost = Number(getValues(FieldName.PURCHASE_COST));
    const replacementCost = Number(getValues(FieldName.REPLACEMENT_COST));

    if (!purchaseCost) {
      return FieldName.PURCHASE_COST;
    }

    if (displayReplacementCost && !replacementCost) {
      return FieldName.REPLACEMENT_COST;
    }

    const isReplacementMode = getValues(FieldName.PRICE_BASED_ON) === PriceBasedOn.REPLACEMENT;

    return isReplacementMode ? FieldName.PRICING_MARGIN : FieldName.MARGIN;
  };

  const onBlurQuantity = async (event?: FocusEvent<HTMLInputElement>) => {
    // Blur caused by activating Save: the save path recomputes authoritatively,
    // so skip the wasted pricing preview. Normal blur-while-editing still fires it.
    if (isSubmitButtonBlur(event)) {
      return;
    }

    // A Select SPA press keeps focus. Read before the await: the press latch lasts one task.
    const isLeavingForSelectSpa = isSelectSpaPressed();
    const quantityChanged = Number(quantityValue) !== previousQuantity;

    // An agreement's cost is quantity-dependent, so a held pick re-prices on any quantity change.
    if (quantityChanged && getValues('selectedSpa')) {
      forceNextPrices();
    }

    await runCalculatePrices();

    if (!quantityChanged) {
      return;
    }

    setPreviousQuantity(Number(getValues(FieldName.QUANTITY)));

    if (!isLeavingForSelectSpa) {
      setFocusInput(getFocusFieldAfterPricing());
    }
  };

  // Offered: the pick moves to its row in the new UOM. Not offered: it stays and the SPA card flags
  // it. Save is never blocked. While the engine is paused the BE refuses the list, so the pick stays.
  const reaskSelectedSpa = async () => {
    const heldPick = getValues('selectedSpa') as ISelectedSpaFormValue | null;

    if (!heldPick?.spaCostSource || getValues('isCostingCustom') || pricingWindowMessage) {
      return;
    }

    const reaskId = ++selectedSpaReaskIdRef.current;
    const request = buildSelectSpaRequest();
    const loadingNotification: INotification = {
      title: t('titleStatus'),
      message: t('messageCalculatePricesInProgress'),
      type: ApiStatus.LOADING,
      position: NotificationPosition.DRAWER,
    };

    showNotification(loadingNotification);

    let lines: ITradeAgreementLine[];

    try {
      lines = (await fetchTradeAgreements(request)).records;
    } catch (error) {
      if (reaskId === selectedSpaReaskIdRef.current) {
        showErrorNotification(
          { title: t('errorTitleTradeAgreementsFetch') },
          NotificationPosition.DRAWER,
          error as AxiosError
        );
      }

      return;
    }

    // Last change wins: a later re-ask owns the notification and the pick by now.
    if (reaskId !== selectedSpaReaskIdRef.current) {
      return;
    }

    const line = getTransientLine(lines);

    // A `line_error` 200 is no answer, as in the drawer.
    if (isTradeAgreementsLineError(line)) {
      showErrorNotification(
        {
          title: t('errorTitleTradeAgreementsFetch'),
          message: withTradeAgreementsLineMessage(t('messageSelectedSpaUomCheckFailed'), line),
        },
        NotificationPosition.DRAWER
      );

      return;
    }

    // Only its own toast: another action may have taken the slot meanwhile.
    if (getNotification() === loadingNotification) {
      clearNotification();
    }

    const isPickUnchanged =
      getTradeAgreementRowId(getValues('selectedSpa')) === getTradeAgreementRowId(heldPick);

    if (!isPickUnchanged) {
      return;
    }

    const heldAgreement = findHeldAgreement(line?.tradeAgreements ?? [], heldPick);

    setIsSelectedSpaNotOffered(!heldAgreement);

    if (heldAgreement) {
      await selectSpa(heldAgreement);
    }
  };

  const onChangeUom = async (_, value) => {
    if (!value) {
      return;
    }

    validateQuantity(quantityValue, value);

    // /prices first: its SPA flag is part of the list request.
    await runCalculatePrices(reaskSelectedSpa);
  };

  const runCalculatePrices = async (afterPrices?: () => Promise<void>) => {
    // Alternate quotes price externally from number + manufacturer alone, so an
    // unverified part must not dead-end here when its purchase cost is empty —
    // e.g. cleared by a UOM the vendor returned all-null costs for.
    const shouldCalculatePrices =
      enabledPricesCall &&
      (partValue?.verified || purchaseCostValue || isAlternateVersionEnabled) &&
      quantityValue &&
      uomValue;

    if (!shouldCalculatePrices) {
      return;
    }

    setIsValidating(true);

    try {
      await costContextCalculatePrices();
      // Still inside isValidating, which keeps the UOM and the pricing fields disabled meanwhile.
      await afterPrices?.();
    } catch (error) {
      handleError(error as AxiosError, t('errorTitleCalculatePrices'));
    } finally {
      setIsValidating(false);
    }
  };

  const validatePart = async (
    part?: Partial<IPart>,
    manufacturer?: IManufacturer,
    unverifiedOnly = false
  ) => {
    if (!part?.number) {
      return;
    }

    showNotification({
      title: t('titleStatus'),
      message: t('messagePartValidationInProgress'),
      type: ApiStatus.LOADING,
      position: NotificationPosition.DRAWER,
    });
    setIsValidating(true);

    // Part / manufacturer change: drop the previous part's quantity-breaks flag so it can't leak
    // into the new part. The validate response re-seeds it (via processPricingResponse); if that
    // part has no price data (e.g. unverified T-item) it stays unknown ⇒ fail open. The Select SPA
    // flag goes the same way, but unknown hides the action.
    resetQuantityBreaks();
    resetSpaSelectionAvailable();

    // Before the response lands: a held pick would keep the old agreement's cost on the new part.
    setValue('selectedSpa', null);
    setIsSelectedSpaNotOffered(false);

    setCostData((prev) => ({
      ...prev,
      costLocked: false,
      costQuantityDiscountsApplied: null,
      costSourceDiscountApplyTo: null,
      costSourceDiscountType: null,
      costSourceDiscountValue: null,
      costSourceId: null,
      costSourcePricingProfileId: null,
      costSourcePricingProfileName: null,
      costSourcePricingProfilePricingType: null,
      ...EMPTY_VENDOR_QUOTE_COST_DATA,
      costSourceStatus: null,
      costSourceType: null,
      costSourceVendorQuoteNumber: null,
      defaultPurchaseCost: null,
      externalPurchaseCost: null,
      externalRepCostEndDate: null,
      externalRepCostRegistrationNum: null,
      externalRepCostSource: null,
      externalRepCostSourceName: null,
      externalReplacementCost: null,
      externalSpaCost: null,
      externalSpaCostEndDate: null,
      externalSpaCostRegistrationNum: null,
      externalSpaCostSource: null,
      externalSpaCostSourceName: null,
      replacementCost: null,
      replacementStandardCost: null,
      repCostRegistrationNum: null,
      repCostEndDate: null,
      selectedSpa: null,
    }));

    setPriceData((prev) => ({
      ...prev,
      externalSellPriceEndDate: null,
      externalSellPriceSource: null,
      externalSellPriceSourceName: null,
      priceLocked: false,
      priceQuantityDiscountsApplied: null,
      priceSourceDiscountType: null,
      priceSourceId: null,
      priceSourcePricingProfileId: null,
      priceSourcePricingProfileName: null,
      priceSourceStatus: null,
      priceSourceType: null,
    }));

    try {
      const currentPricingDate = getValues('pricingDate');
      const pricingDateStr = currentPricingDate
        ? dayjs(currentPricingDate).format(DB_DATE_FORMAT)
        : undefined;
      const currentPricingStrategy = isAlternateVersionEnabled
        ? getValues('pricingStrategy') ?? null
        : null;

      const { record } = await validatePartAction(
        part?.number,
        manufacturer?.id || part?.manufacturerId || null,
        part?.vendorNumber || null,
        entityId,
        unverifiedOnly,
        pricingDateStr,
        currentPricingStrategy
      );

      setEnabledPricesCall(!disabledPriceCall);

      if (record) {
        setUomOptions(mapUomOptions(record?.uoms));
        onValidatedPartResponse(record);
      }

      // Also without a record: the drawer's Save and close lock on it.
      setIsValidating(false);
      clearNotification();
    } catch (error) {
      handleError(error as AxiosError, t('errorTitlePartValidation'));
    }
  };

  const submitFormOnEnter = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isEnterKeyPress(event) || isLoadingNotificationDisplayed || isValidating) {
      return;
    }

    // Enter is a submit: mark intent so the blur fired when the focused field disables for the save
    // (disable-on-submit effect) is recognised as submit-caused and skips its /quote/part/prices
    // preview — mirrors the mouse Save path in StepButtons/FormView.
    markSubmitIntent();

    // Shift+Enter is the keyboard equivalent of Save & Add Next and runs that button's own step
    // options, so it keeps the same validation and step mechanics. Where the button isn't offered
    // (edit mode, taxonomy variant) the options are null and Shift+Enter stays plain Enter.
    goToNextStep(
      isShiftEnterKeyPress(event) && saveAndAddNextOptions
        ? saveAndAddNextOptions
        : { onClick: onSubmit }
    );
  };

  const handleOnClear = () => {
    onReset();
    setHintOpen(true);
    setFocusInput(FieldName.PART);
    setUomOptions(uoms as unknown as IOption[]);
    onResetValidatedPartResponse();
  };

  const onReset = (part = null, manufacturer = null) => {
    reset({
      part: part,
      manufacturer: manufacturer,
      description: null,
      quantity: null,
      uom: null,
      standardCost: null,
      purchaseCost: null,
      margin: null,
      price: null,
      costSource: {
        vendorQuoteNumber: null,
      },
      isReplacementCostCustom: false,
      isCostingCustom: false,
      prioritizeLegacyCost: false,
      replacementCost: null,
      replacementCostNumber: null,
      priceBasedOn: PriceBasedOn.QUOTED,
      pricingMargin: null,
      replacementStandardCost: null,
      selectedSpa: null,
      pricingDate: getValues('pricingDate'),
      pricingStrategy: getValues('pricingStrategy'),
    });
    setConfig(null);
    setCostData({});
    setPriceData({});
    setQuantityWarnings([]);
    setUnverifiedPartNumber(part?.number || null);
    // Clearing the form drops the part, so its engine flags must not survive.
    resetQuantityBreaks();
    resetSpaSelectionAvailable();
    setIsSelectedSpaNotOffered(false);
  };

  const handleError = (error: AxiosError, errorTitle: string) => {
    const errorData = error.response?.data as AxiosErrorData;

    const isExternalError = EXTERNAL_REQUEST_ERROR_CODE === errorData?.errorCode;

    const title = isExternalError ? t('errorTitleExternalPricing') : errorTitle;
    const message = isExternalError ? errorData.error : error?.message;
    const position = isExternalError ? NotificationPosition.TOP : NotificationPosition.DRAWER;

    if (isExternalError) {
      onClose();
    }

    showErrorNotification(
      {
        title,
        message,
      },
      position
    );

    setIsValidating(false);
  };

  return {
    isOpen,
    entityId,
    hintOpen,
    closeHint: () => setHintOpen(false),
    unverifiedPartNumber,
    verifiedPart,
    isValidating,
    uomOptions,
    costData,
    defaultShippingFields,
    focusInput,
    setFocusInput,
    emptyPartValues,
    isSubmitting,
    onChangePartNumber,
    onKeyDownPartNumber,
    onChangeManufacturer,
    onChangeQuantity,
    onBlurQuantity,
    onChangeUom,
    submitFormOnEnter,
    quantityWarnings,
  };
};

export const [UpsertLineFormProvider, useUpsertLineFormContext] = constate(useUpsertLineForm);
